import { Activity, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'underscore';
import classNames from 'classnames';

import { IInputWithOverlayProps } from './input-with-overlay.types';
import { mergeRefs } from '@/utils/merge-refs';
import { isDefined } from '@/utils/is-defined';
import useDimensionsObserver from '@/utils/use-dimensions-observer';

import './input-with-overlay.css';

// this input wrapper is used to allow the input have an overlay on top of it without interfering with the input itself
const InputWithOverlay = ({
  label = '',
  jsxLabel,
  inputProps,
  renderInnerContent,
  children,
  wrapperClass,
  onDebounce,
  debounceTime = 100,
  enableChildrenPointerEvents = true,
  innerContentClassName = '',
  innerContentStyle = {},
  status,
  dirtyError,
  inputRef,
  inputHeight,
  inputWidth,
  containerRef,
  border,
  innerContentRef,
  unhoveredElipses,
  background,
  hoverBackground,
  hoverBorder,
  focusBorder,
  'data-component': dataComponent,
  isBorderless = false,
  design = 'GlobalIcoText3',
  designSize = 'large',
  typography: propsTypography,
  padRightInnerContent,
}: IInputWithOverlayProps) => {
  const { style: inputStyle, ...restInputProps } = inputProps || {};
  const error = status === 'error';
  const success = status === 'success';

  const typography = propsTypography ?? (design ? 'P2' : undefined);

  const [dirty, setDirty] = useState(false);
  const [hasError, setHasError] = useState(error || false);
  const [focusTrigger, setFocusTrigger] = useState(0);

  const innerContentRefLocal = useRef<HTMLDivElement | null>(null);

  // keep a ref to the latest onDebounce so the stable debounce
  // function always calls the current callback, not the one captured at mount.
  const onDebounceRef = useRef(onDebounce);
  useEffect(() => {
    onDebounceRef.current = onDebounce;
  }, [onDebounce]);

  const handleDebounce = useRef(
    debounce((value: string) => {
      onDebounceRef.current?.(value);
    }, debounceTime)
  ).current;

  const onChangeRef = useRef(inputProps?.onChange);
  const onFocusRef = useRef(inputProps?.onFocus);
  const onBlurRef = useRef(inputProps?.onBlur);
  useEffect(() => {
    onChangeRef.current = inputProps?.onChange;
    onFocusRef.current = inputProps?.onFocus;
    onBlurRef.current = inputProps?.onBlur;
  }, [inputProps?.onChange, inputProps?.onFocus, inputProps?.onBlur]);

  // memoize callbacks so a new function reference isn't created every render.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeRef.current?.(e);
      handleDebounce(e.target.value);
    },
    [handleDebounce]
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setFocusTrigger((prev) => prev + 1);
    onFocusRef.current?.(e);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setDirty(true);
    setFocusTrigger((prev) => prev + 1);
    onBlurRef.current?.(e);
  }, []);

  useEffect(() => {
    setHasError(!!error);
  }, [error]);

  // keep dirtyError in a ref so the effect only re-runs when dirty
  // changes, not every time the caller passes a new inline function reference.
  const dirtyErrorRef = useRef(dirtyError);
  useEffect(() => {
    dirtyErrorRef.current = dirtyError;
  }, [dirtyError]);

  useEffect(() => {
    if (!dirty) return;
    dirtyErrorRef.current?.({ dirty, setHasError });
  }, [dirty, focusTrigger]);

  // if padRightInnerContent, then track the width of innerContentRef
  // and add paddingRight to the input that is the value of that width
  const innerContentBounds = useDimensionsObserver({
    ref: innerContentRefLocal,
    customOptions: {
      pause: !renderInnerContent || !padRightInnerContent || !innerContentRefLocal?.current?.children.length,
    },
  });
  // 12 is just a little spacing between the last letter of the input and the button
  const innerContentWidth = innerContentBounds?.width ? innerContentBounds?.width + 12 : undefined;

  // memoize the style object so a new reference isn't created every render.
  const inputComputedStyle = useMemo(
    () => ({
      ...inputStyle,
      ...(inputHeight ? { '--input-with-overlay-input-height': inputHeight } : {}),
      ...(inputWidth ? { '--input-with-overlay-input-width': inputWidth } : {}),
      ...(border && !isBorderless ? { '--input-with-overlay-border': border } : {}),
      ...(background ? { '--input-with-overlay-background': background } : {}),
      ...(hoverBackground ? { '--input-with-overlay-hover-bg-color': hoverBackground } : {}),
      ...(hoverBorder && !isBorderless ? { '--input-with-overlay-hover-border': hoverBorder } : {}),
      ...((focusBorder || hoverBorder) && !isBorderless
        ? { '--input-with-overlay-focus-border': focusBorder || hoverBorder }
        : {}),
      ...(isDefined(innerContentWidth)
        ? {
            paddingRight: `${innerContentWidth}px`,
          }
        : {}),
    }),
    [
      inputStyle,
      inputHeight,
      inputWidth,
      border,
      isBorderless,
      background,
      hoverBackground,
      hoverBorder,
      focusBorder,
      innerContentWidth,
    ]
  );

  return (
    <div
      data-component={dataComponent || 'InputWithOverlay'}
      className={classNames('input-with-overlay-wrapper', wrapperClass)}
      ref={containerRef}
    >
      <Activity mode={!!label && !jsxLabel ? 'visible' : 'hidden'}>
        <label className="L2">{label}</label>
      </Activity>

      <Activity mode={jsxLabel ? 'visible' : 'hidden'}>
        <>{jsxLabel?.()}</>
      </Activity>

      <div className="input-with-overlay-input-wrapper">
        <input
          {...restInputProps}
          style={inputComputedStyle}
          ref={inputRef}
          className={classNames(
            'input-with-overlay',
            typography,
            {
              error: hasError,
              success,
              borderless: isBorderless,
              ['unhovered-ellipses']: unhoveredElipses,
              [`input-with-overlay-${design}`]: design,
              [`input-with-overlay-${designSize}`]: designSize,
              [`input-with-overlay-explicit-border`]: hoverBorder,
              [`input-with-overlay-explicit-hover-border`]: hoverBorder,
            },
            inputProps?.className
          )}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div
          className={classNames('input-with-overlay-children', innerContentClassName, {
            ['enable-children-pointer-events']: enableChildrenPointerEvents,
          })}
          style={innerContentStyle}
          ref={mergeRefs(innerContentRef, innerContentRefLocal)}
        >
          {renderInnerContent?.()}
        </div>
      </div>
      {children}
    </div>
  );
};

export default InputWithOverlay;
